import type { AuthedRequest } from '../auth/AuthProvider';
import type { AccessMode } from '../users/api';

/** An external system that receives video (ADR-024). */
export interface Integration {
  id: string;
  name: string;
  /** RTSP user name. */
  username: string;
  status: 'active' | 'revoked';
  mode: AccessMode;
  client_ids?: string[];
  site_ids: string[];
  device_ids: string[];
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  /** rtsp://host:port other systems use. */
  rtsp_url: string;
  /** Present after creating, resetting or revealing it. */
  password?: string;
  /** Permissions of the "Integração" profile, shared by every integration (ADR-027). */
  permissions?: string[];
}

export interface IntegrationBody {
  name?: string;
  mode?: AccessMode;
  client_ids?: string[];
  site_ids?: string[];
  device_ids?: string[];
}

export const integrationsKey = ['integrations'] as const;

export async function listIntegrations(req: AuthedRequest): Promise<Integration[]> {
  return (await req<{ items: Integration[] }>('GET', '/api/v1/integrations')).items;
}

const path = (id: string, rest = '') => `/api/v1/integrations/${encodeURIComponent(id)}${rest}`;

export const createIntegration = (req: AuthedRequest, body: IntegrationBody) =>
  req<Integration>('POST', '/api/v1/integrations', body);
export const updateIntegration = (req: AuthedRequest, id: string, body: IntegrationBody) =>
  req<Integration>('PATCH', path(id), body);
export const resetIntegrationPassword = (req: AuthedRequest, id: string) =>
  req<Integration>('POST', path(id, '/password'), {});
/** A camera an integration may receive. */
export interface GrantedCamera {
  number: number;
  device_id: string;
  name: string;
  site: string;
  enabled: boolean;
}

export const integrationCamerasKey = (id: string) => ['integrations', id, 'cameras'] as const;
export async function listIntegrationCameras(
  req: AuthedRequest,
  id: string,
): Promise<GrantedCamera[]> {
  return (await req<{ items: GrantedCamera[] }>('GET', path(id, '/cameras'))).items;
}

/** The integration with its password (audited on the server). */
export const getIntegrationCredentials = (req: AuthedRequest, id: string) =>
  req<Integration>('GET', path(id, '/credentials'));
export const revokeIntegration = (req: AuthedRequest, id: string) =>
  req<Integration>('POST', path(id, '/revoke'), {});
export const deleteIntegration = (req: AuthedRequest, id: string) => req<null>('DELETE', path(id));

/** Host and port of an rtsp://host:port address. */
export function rtspEndpoint(url: string): { host: string; port: string } {
  try {
    const u = new URL(url.replace(/^rtsp:/, 'http:'));
    return { host: u.hostname, port: u.port || '554' };
  } catch {
    return { host: '', port: '554' };
  }
}

/** The address of a camera's stream for other systems (Hikvision style). */
export const cameraPath = (n: number, sub = false) =>
  `/Streaming/Channels/${String(n)}${sub ? '02' : '01'}`;

export const integrationMessages: Record<string, string> = {
  required: 'Informe um nome.',
  too_long: 'Nome longo demais.',
  taken: 'Já existe uma integração com este nome.',
  unknown: 'Um dos locais ou câmeras selecionados não existe mais.',
  out_of_range: 'Seleção grande demais; marque locais inteiros.',
  invalid: 'Valor inválido.',
};
